import { useState, useEffect, useCallback } from 'react';
import { Megaphone, Bell, CheckCheck, Filter, Info, AlertTriangle, Clock, Headphones, Trash2 } from 'lucide-react';
import { getNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification, getActiveBroadcasts } from '../api/notificationApi';
import { useToast } from '../components/ToastProvider';

const FETCH_LIMIT = 50;
const BROADCAST_FETCH_LIMIT = 20;

// Real notification-api enum: info/success/warning/reminder/support — not the
// concept's fictional booking/review/payment/system categories.
const TYPE_ICON = { info: Info, success: CheckCheck, warning: AlertTriangle, reminder: Clock, support: Headphones };
const TYPE_LABEL = { info: 'Info', success: 'Success', warning: 'Warning', reminder: 'Reminder', support: 'Support' };

function formatRelativeTime(value) {
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function NotificationsPage() {
  const { showToast } = useToast();
  const [tab, setTab] = useState('broadcast');
  const [typeFilter, setTypeFilter] = useState('all');

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    return getNotifications({ limit: FETCH_LIMIT })
      .then((data) => {
        setNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
        setUnreadCount(data?.unreadCount || 0);
      })
      .catch(() => { setNotifications([]); setUnreadCount(0); });
  }, []);

  const loadBroadcasts = useCallback(() => {
    return getActiveBroadcasts({ limit: BROADCAST_FETCH_LIMIT })
      .then((data) => setBroadcasts(Array.isArray(data?.notifications) ? data.notifications : []))
      .catch(() => setBroadcasts([]));
  }, []);

  useEffect(() => {
    Promise.all([load(), loadBroadcasts()]).finally(() => setLoading(false));
  }, [load, loadBroadcasts]);

  async function markAllRead() {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      showToast('All notifications marked as read.');
    } catch {
      showToast('Could not mark all as read.', 'error');
    }
  }

  async function markRead(id) {
    setBusyId(id);
    try {
      await markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      showToast('Could not mark as read.', 'error');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(id, e) {
    e.stopPropagation();
    setBusyId(id);
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      showToast('Notification deleted.');
    } catch {
      showToast('Could not delete notification.', 'error');
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <div className="w-10 h-10 rounded-full border-4 border-[#e8f0fe] dark:border-[#1a2e4c] border-t-[#1a73e8] animate-spin" />
      </div>
    );
  }

  const typesPresent = Array.from(new Set(notifications.map((n) => n.type)));
  const filteredNotifications = typeFilter === 'all' ? notifications : notifications.filter((n) => n.type === typeFilter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">Notifications</h2>
          <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6]">{unreadCount === 0 ? 'All caught up' : `${unreadCount} unread`}</span>
        </div>
        <button onClick={markAllRead} disabled={unreadCount === 0}
          className="px-3.5 py-1.5 rounded-full text-xs font-semibold border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134] disabled:opacity-40 cursor-pointer flex items-center gap-1.5">
          <CheckCheck className="w-3.5 h-3.5" /> Mark all read
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 space-y-4">
          <div className="inline-flex p-1 bg-[#f1f3f4] dark:bg-[#202124] rounded-full">
            <button onClick={() => setTab('broadcast')}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${tab === 'broadcast' ? 'bg-white dark:bg-[#303134] text-[#1a73e8] shadow-sm' : 'text-[#5f6368] dark:text-[#9aa0a6]'}`}>
              <Megaphone className="w-3.5 h-3.5" /> Broadcast
            </button>
            <button onClick={() => setTab('notifications')}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${tab === 'notifications' ? 'bg-white dark:bg-[#303134] text-[#1a73e8] shadow-sm' : 'text-[#5f6368] dark:text-[#9aa0a6]'}`}>
              <Bell className="w-3.5 h-3.5" /> Notifications
              {unreadCount > 0 && <span className="text-[10px] font-bold bg-[#EA4335] text-white rounded-full px-1.5">{unreadCount}</span>}
            </button>
          </div>

          {tab === 'broadcast' && (
            broadcasts.length === 0 ? (
              <EmptyState icon={Megaphone} title="No broadcasts" subtitle="Active broadcast announcements will show up here." />
            ) : (
              <div className="space-y-3">
                {broadcasts.map((b) => (
                  <div key={b._id} className="google-card p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                    <div className="flex items-center gap-2 text-[11px] font-bold text-amber-700 dark:text-amber-300">
                      <Megaphone className="w-3.5 h-3.5" /> {b.title}
                    </div>
                    <p className="text-xs text-amber-900/80 dark:text-amber-200/80 mt-1.5">{b.message}</p>
                    <span className="text-[10px] text-amber-700/70 dark:text-amber-400/70 mt-1.5 block">{formatRelativeTime(b.createdAt)}</span>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === 'notifications' && (
            filteredNotifications.length === 0 ? (
              <EmptyState icon={Bell} title="No notifications" subtitle="Booking, review and payout alerts will show up here." />
            ) : (
              <div className="space-y-2.5">
                {filteredNotifications.map((n) => {
                  const Icon = TYPE_ICON[n.type] || Bell;
                  return (
                    <button key={n._id} onClick={() => !n.read && markRead(n._id)} disabled={busyId === n._id}
                      className={`w-full text-left google-card p-4 flex items-start gap-3 cursor-pointer transition-colors disabled:opacity-60 ${
                        n.read ? 'bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]' : 'bg-[#e8f0fe] dark:bg-[#1a2e4c] border border-blue-200 dark:border-blue-900'
                      }`}>
                      <div className="p-2 rounded-xl bg-white dark:bg-[#303134] text-[#1a73e8] shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] truncate">{n.title}</h4>
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#1a73e8] shrink-0" />}
                        </div>
                        <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-0.5">{n.message}</p>
                        <span className="text-[10px] text-[#9aa0a6] mt-1 block">{formatRelativeTime(n.createdAt)}</span>
                      </div>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleDelete(n._id, e)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleDelete(n._id, e); }}
                        className="p-1.5 rounded-lg text-[#9aa0a6] hover:text-[#EA4335] hover:bg-red-50 dark:hover:bg-red-950/30 shrink-0 cursor-pointer"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </span>
                    </button>
                  );
                })}
              </div>
            )
          )}
        </div>

        <div className="lg:col-span-4 space-y-4">
          <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-3">Overview</h3>
            <div className="grid grid-cols-2 gap-2.5 text-xs">
              <div className="p-3 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                <span className="text-[#5f6368] dark:text-[#9aa0a6] block">Total</span>
                <span className="text-lg font-bold text-[#202124] dark:text-[#e8eaed]">{notifications.length}</span>
              </div>
              <div className="p-3 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                <span className="text-[#5f6368] dark:text-[#9aa0a6] block">Unread</span>
                <span className="text-lg font-bold text-[#1a73e8]">{unreadCount}</span>
              </div>
              <div className="p-3 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                <span className="text-[#5f6368] dark:text-[#9aa0a6] block">Broadcasts</span>
                <span className="text-lg font-bold text-[#202124] dark:text-[#e8eaed]">{broadcasts.length}</span>
              </div>
              <div className="p-3 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                <span className="text-[#5f6368] dark:text-[#9aa0a6] block">Types</span>
                <span className="text-lg font-bold text-[#202124] dark:text-[#e8eaed]">{typesPresent.length}</span>
              </div>
            </div>
          </div>

          <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-3 flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-[#1a73e8]" /> Filter by Type
            </h3>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setTypeFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer ${typeFilter === 'all' ? 'bg-[#1a73e8] text-white' : 'bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#e8eaed] dark:hover:bg-[#3c4043]'}`}>
                All
              </button>
              {typesPresent.map((t) => (
                <button key={t} onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer ${typeFilter === t ? 'bg-[#1a73e8] text-white' : 'bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#e8eaed] dark:hover:bg-[#3c4043]'}`}>
                  {TYPE_LABEL[t] || t}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="google-card py-14 flex flex-col items-center text-center bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
      <div className="w-14 h-14 rounded-2xl bg-[#f1f3f4] dark:bg-[#202124] flex items-center justify-center text-[#9aa0a6] mb-3">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed]">{title}</h3>
      <p className="text-xs text-[#9aa0a6] mt-1 max-w-xs">{subtitle}</p>
    </div>
  );
}
